import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaArrowLeft, 
  FaArrowRight, 
  FaBone,
  FaSearchPlus,
  FaHospital
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const Orthopedics = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };
  
  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  // Orthopedic product data
  const products = [
    {
      id: 'ortho-1',
      name: 'Orthopedic Health Formula',
      description: 'Comprehensive orthopedic health formula for bone and joint support.',
      dosageForm: 'Tablets',
      category: 'Bone & Joint Care',
      image: process.env.PUBLIC_URL + '/product_img/orthopedic.png'
    }
  ];

  // Function to handle product selection for detail view
  const openProductDetails = (product) => {
    setSelectedProduct(product);
    window.scrollTo(0, 0);
  };

  // Function to close product details
  const closeProductDetails = () => {
    setSelectedProduct(null);
  };

  return (
    <div>
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orthopedic to-amber-600 text-white py-24">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center mb-4">
              <Link to="/products" className="flex items-center text-white hover:text-amber-100 transition-colors">
                <FaArrowLeft className="mr-2" /> Back to Categories
              </Link>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold mb-6">Orthopedic Products</h1>
                <p className="text-xl text-amber-50 max-w-3xl mb-8">
                  Our specialized range of orthopedic medicines and supplements designed 
                  to improve bone, joint, and muscle health at every stage of life.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a href="#products" className="inline-flex items-center bg-white text-orthopedic px-6 py-3 rounded-lg font-medium hover:bg-amber-50 transition-colors">
                    View Products <FaArrowRight className="ml-2" />
                  </a>
                  <Link to="/contact" className="inline-flex items-center bg-transparent border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors">
                    Request Information
                  </Link>
                </div>
              </div>
              <div className="hidden lg:block">
                <motion.div 
                  className="relative"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <div className="absolute -top-6 -left-6 bg-white/20 rounded-full w-32 h-32 backdrop-blur-sm"></div>
                  <div className="absolute -bottom-10 -right-10 bg-white/10 rounded-full w-40 h-40 backdrop-blur-sm"></div>
                  <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20">
                    <div className="flex justify-center">
                      <img 
                        src={process.env.PUBLIC_URL + "/product_img/orthopedic.png"} 
                        alt="Orthopedic Product" 
                        className="h-32 w-32 object-contain mb-4"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'block';
                        }}
                      />
                      <FaBone 
                        className="text-white text-6xl hidden" 
                        style={{ display: 'none' }}
                      />
                    </div>
                    <div className="text-center text-white">
                      <div className="font-bold text-2xl mb-1">Orthopedic Excellence</div>
                      <div className="text-amber-100">Joint & Bone Health Specialists</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Product Detail View */}
      {selectedProduct && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="py-16 bg-gray-50"
        >
          <div className="container-custom">
            <button 
              onClick={closeProductDetails}
              className="flex items-center text-orthopedic hover:text-amber-700 mb-8 transition-colors"
            >
              <FaArrowLeft className="mr-2" /> Back to Products
            </button>
            
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-8 lg:p-12">
                  <div className="flex items-center mb-6">
                    <span className="bg-orthopedic/10 text-orthopedic text-xs px-3 py-1 rounded-full">
                      {selectedProduct.category}
                    </span>
                    <span className="ml-4 bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-full">
                      {selectedProduct.dosageForm}
                    </span>
                  </div>

                  <h2 className="text-3xl font-bold text-gray-800 mb-4">{selectedProduct.name}</h2>
                  <p className="text-gray-600 mb-8 text-lg">
                    Our Orthopedic Health Formula is designed to provide comprehensive support for bone and joint health. 
                    This advanced formulation contains essential nutrients, vitamins, and minerals that contribute to 
                    maintaining strong bones and flexible joints throughout life.
                  </p>

                  <div className="mb-8">
                    <h3 className="text-lg font-bold text-gray-800 mb-3">Key Benefits</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <span className="text-orthopedic mr-2 mt-1">•</span>
                        <span className="text-gray-600">Supports overall bone density and strength</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orthopedic mr-2 mt-1">•</span>
                        <span className="text-gray-600">Promotes joint flexibility and comfort</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orthopedic mr-2 mt-1">•</span>
                        <span className="text-gray-600">Aids in cartilage maintenance and joint cushioning</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orthopedic mr-2 mt-1">•</span>
                        <span className="text-gray-600">Contains nutrients essential for skeletal system health</span>
                      </li>
                    </ul>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-lg font-bold text-gray-800 mb-2">Recommended Use</h3>
                    <p className="text-gray-600">Take 1 tablet twice daily with meals or as directed by your healthcare professional.</p>
                  </div>

                  <div>
                    <Link 
                      to="/contact" 
                      className="inline-flex items-center bg-orthopedic text-white px-6 py-3 rounded-lg font-medium hover:bg-amber-600 transition-colors"
                    >
                      Request More Information
                    </Link>
                  </div>
                </div>

                <div className="bg-amber-50 p-8 lg:p-12 flex items-center justify-center">
                  <div className="text-center">
                    <div className="mb-6 w-48 h-48 bg-white rounded-full shadow-lg mx-auto flex items-center justify-center">
                      <img 
                        src={selectedProduct.image} 
                        alt={selectedProduct.name} 
                        className="h-32 w-32 object-contain"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'block';
                        }}
                      />
                      <FaBone 
                        className="text-orthopedic text-6xl hidden" 
                        style={{ display: 'none' }}
                      />
                    </div>
                    <h3 className="text-xl font-bold text-orthopedic mb-2">{selectedProduct.name}</h3>
                    <p className="text-amber-800">{selectedProduct.category} - {selectedProduct.dosageForm}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Products List */}
      <div id="products" className="container-custom py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Orthopedic Solutions</h2>
          <div className="w-24 h-1 bg-orthopedic mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive products for bone, joint, and muscle care developed with advanced pharmaceutical technology
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              variants={fadeIn}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2"
            >
              <div className="h-2 bg-orthopedic"></div>
              <div className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <h3 className="text-2xl font-bold text-orthopedic">{product.name}</h3>
                  <span className="bg-orthopedic/10 text-orthopedic text-xs px-2 py-1 rounded-full">
                    {product.dosageForm}
                  </span>
                </div>

                <div className="mb-8 flex justify-center">
                  <div className="w-32 h-32 bg-amber-50 rounded-full flex items-center justify-center">
                    <img 
                      src={product.image} 
                      alt={product.name} 
                      className="h-24 w-24 object-contain"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'block';
                      }}
                    />
                    <FaBone 
                      className="text-orthopedic text-6xl hidden" 
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>

                <p className="text-gray-600 mb-6 text-center text-lg">
                  {product.description}
                </p>
                
                <div className="text-center mb-4">
                  <span className="inline-block bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium">
                    {product.category}
                  </span>
                </div>
                
                <div className="text-center">
                  <button 
                    onClick={() => openProductDetails(product)}
                    className="inline-flex items-center justify-center bg-orthopedic text-white px-6 py-3 rounded-lg font-medium hover:bg-amber-600 transition-colors w-full"
                  >
                    View Details <FaSearchPlus className="ml-2" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Key Features Section */}
      <div className="bg-gray-50 py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose Our Orthopedic Products</h2>
            <div className="w-24 h-1 bg-orthopedic mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our orthopedic range is developed with the highest quality standards
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="bg-white p-8 rounded-lg shadow-md"
            >
              <div className="w-16 h-16 bg-orthopedic/10 rounded-full flex items-center justify-center mb-6 mx-auto">
                <FaBone className="text-orthopedic text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Complete Bone Support</h3>
              <p className="text-gray-600 text-center">
                Our formulations provide comprehensive support for bone density, strength, and overall skeletal health.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="bg-white p-8 rounded-lg shadow-md"
            >
              <div className="w-16 h-16 bg-orthopedic/10 rounded-full flex items-center justify-center mb-6 mx-auto">
                <FaHospital className="text-orthopedic text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Clinically Developed</h3>
              <p className="text-gray-600 text-center">
                Created by orthopedic specialists based on clinical research and evidence-based medicine.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="bg-white p-8 rounded-lg shadow-md"
            >
              <div className="w-16 h-16 bg-orthopedic/10 rounded-full flex items-center justify-center mb-6 mx-auto">
                <FaArrowRight className="text-orthopedic text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Enhanced Absorption</h3>
              <p className="text-gray-600 text-center">
                Advanced formulation technology ensures optimal nutrient absorption and utilization for maximum benefit.
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-white">
        <div className="container-custom">
          <div className="bg-gradient-to-r from-orthopedic to-amber-600 rounded-xl p-12 text-white text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Need Orthopedic Product Information?</h2>
              <p className="text-xl mb-8 max-w-3xl mx-auto">
                Our team is ready to assist healthcare professionals with detailed product information, samples, and support.
              </p>
              <Link 
                to="/contact" 
                className="inline-block bg-white text-orthopedic font-bold py-4 px-8 rounded-md shadow-lg hover:bg-gray-100 transition-colors"
              >
                Contact Us
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Orthopedics;
